import RemoteRequest from "./internal/remote-request";
import Router from "./internal/router";
import { RequestHandler } from "./internal/request-handler";
import { extractTransportOpts } from "./internal/transport-construction";
// @ts-ignore
import { registerTransport, getKnownTransportTypes } from "./transport-shortcut-registration";
const DefaultGlobalRequestOpts = {
    rsvp: true,
    outboundStack: true,
};
const DefaultChannel = 'CommonChannel';
export default class WranggleRpc {
    constructor(rpcOpts) {
        this._globalRequestOpts = {};
        this._requestOptsByMethod = {};
        this.requestHandler = new RequestHandler();
        this.router = new Router({ onValidatedRequest: this.requestHandler.onValidatedRequest.bind(this.requestHandler) });
        if (typeof rpcOpts === 'string') { // hmm, not an especially useful constructor signature but looks good in the readme example
            rpcOpts = { transport: rpcOpts };
        }
        if (rpcOpts) {
            const transportOpts = extractTransportOpts(rpcOpts);
            if (transportOpts) {
                this.router.useTransport(transportOpts, rpcOpts.channel || DefaultChannel);
            }
        }
        this.opts(Object.assign({ globalRequestOpts: DefaultGlobalRequestOpts }, (rpcOpts || {})));
    }
    /**
     * Incoming requests are passed to methods on the specified `delegate` object if it passes the `DelegateOpts` filters specified.
     *
     */
    addRequestHandlerDelegate(delegate, opts) {
        this.requestHandler.addRequestHandlerDelegate(delegate, opts);
    }
    /**
     * Add a function to handle incoming request messages.
     *
     * @param methodName. String that is a legitimate js variable name. (No spaces and such.)
     * @param fn Function that runs the remotely-passed arguments. This function can return a value or a promise.
     * @param context. Optional. Sets the "this" context of your function. Note/reminder: arrow functions do not have a "this" binding,
     *   so use a full "function" when setting this option.
     */
    addRequestHandler(methodName, fn, opts) {
        this.requestHandler.addRequestHandler(methodName, fn, opts);
    }
    /**
     * Shortcut to addRequestHandler. Accepts an object of methodName-function pairs.
     * Note: methodName/keys starting with underscore "_" are skipped here, but can be added with a direct call to `addRequestHandler`
     * @param fnByMethodName
     * @param context
     */
    addRequestHandlers(fnByMethodName, opts) {
        this.requestHandler.addRequestHandlers(fnByMethodName, opts);
    }
    /**
     * Set the RpcTransport for sending and receiving messages.
     *
     * @param transportOpts
     * @param channel
     */
    useTransport(transportOpts, channel) {
        this.router.useTransport(transportOpts, channel);
    }
    /**
     * Update options. Accepts same values as during WranggleRpc construction.
     * WARNING: currently clobbers, does not merge. todo: improve
     * @param opts
     */
    opts(opts) {
        let { senderId, globalRequestOpts, debug, channel, preparseAllIncomingMessages, transport } = opts;
        if (senderId) { // should be renamed
            this.router.setEndpointUid(senderId);
        }
        if (opts.hasOwnProperty('debug')) {
            this.router.updateDebugHandler(opts.debug);
        }
        if (preparseAllIncomingMessages || debug) {
            this.router.routerOpts({ preparseAllIncomingMessages, debug });
        }
        if (globalRequestOpts) {
            this._globalRequestOpts = Object.assign({}, DefaultGlobalRequestOpts, this._globalRequestOpts, globalRequestOpts);
        }
    }
    /**
     * Returns an object that you use to make your remote calls.
     * All remote calls return a `RemotePromise`.
     *
     * For example, if in the other window you added a request handler:
     *   ```
     *   otherRpc.addRequestHandler('sayHello', name => `Hello ${name}`)
     *   ```
     * You could then call it from the current window:
     *   ```
     *   const remoteControl = thisRpc.remoteInterface();
     *   const remotePromise = remoteControl.sayHello('Bob');
     *   remotePromise.useTimeout(500);
     *   console.log(remotePromise.info());
     *   ```
     *
     * The returned `RemotePromise` behaves as a normal Promise, with some additional features, such as
     * setting timeout and request options, and getting status info.
     */
    remoteInterface() {
        const itself = this;
        return new Proxy({}, {
            get: function (obj, methodName) {
                return (...userArgs) => itself.makeRemoteRequest(methodName, userArgs);
            }
        });
    }
    /**
     * An alternative way to call remote methods.
     * @param methodName
     * @param userArgs
     * @param requestOpts
     */
    makeRemoteRequest(methodName, userArgs, requestOpts = {}) {
        requestOpts = Object.assign({}, this._globalRequestOpts, this._requestOptsByMethod[methodName], requestOpts);
        const req = new RemoteRequest(methodName, userArgs, requestOpts);
        return this.router.sendRemoteRequest(req);
    }
    /**
     *
     * whenever a request to the passed-in methodName is called, it applies the supplied `RequestOpts` options
     * See also setting the `globalRequestOpts` global option. And some options can be applied on `RemotePromise`.
     *
     * @param methodName
     * @param requestOpts
     */
    setDefaultRequestOptsForMethod(methodName, requestOpts) {
        this._requestOptsByMethod[methodName] = Object.assign((this._requestOptsByMethod[methodName] || {}), requestOpts);
    }
    // todo: checkConnectionStatus(opts = <IConnectionStatusOpts>{}): Promise<IConnectionStatus> {
    //   return this.router.checkConnectionStatus(opts); // TODO: not yet implemented
    // }
    getTransport() {
        return this.router.transport;
    }
    stopTransport() {
        const transport = this.getTransport();
        transport && transport.stopTransport();
    }
    /**
     * Stops endpoint and transport and resolves all pending requests as having failed (with the passed-in error record)
     * @param error
     */
    abortEndpoint(error) {
        this.router.abortEndpoint(error);
    }
    /**
     * Calling stopEndpoint tells the WranggleRpc instance to ignore new messages. Useful when multiple instances are sharing
     *  the same transport (using different channels.)
     */
    stopEndpoint() {
        this.router.stopEndpoint();
    }
    getEndpointUid() {
        return this.router.getEndpointUid();
    }
    static registerTransport(transportType, transportFactory) {
        registerTransport(transportType, transportFactory);
    }
    static knownTransportTypes() {
        return getKnownTransportTypes();
    }
}
//# sourceMappingURL=core.js.map